import {Skeleton} from "@mui/material";
import Typography from "@mui/material/Typography";
import {ReactElement} from "react";
import {ElementApiClient} from "../../../../../../common/apis/element/ElementApiClient";
import {ElementDto} from "../../../../../../common/apis/element/ElementDto";
import {ElementFilterDto} from "../../../../../../common/apis/element/ElementFilterDto";
import GraphApiClient from "../../../../../../common/apis/GraphApiClient";
import {ConnectableConceptInfoDto} from "../../../../../../common/apis/relationship/ConnectableConceptInfoDto";
import {ConnectableConceptType} from "../../../../../../common/apis/relationship/ConnectableConceptType";
import {RelationshipApiClient} from "../../../../../../common/apis/relationship/RelationshipApiClient";
import {RelationshipCreateDto} from "../../../../../../common/apis/relationship/RelationshipDto";
import {ReferenceDefinitionDto, RelationshipDirection} from "../../../../../../common/apis/stereotype/ReferenceDefinitionDto";
import {UUIDUtils} from "../../../../../../common/UUIDUtils";
import SingleSelectField from "../../../../../../components/fields/select/SingleSelectField";
import {t} from "../../../../../../store/localization/TranslMessasge";
import ReferenceDefinitionGraphSearcher from "../../reference/ReferenceDefinitionGraphSearcher";
import {StereotypesTranslationKey} from "../../StereotypesTranslationKey";
import {SMART_REFERENCE_SELECT_FIELD_COMPONENT_TYPE, SmartReferenceSelectField} from "../model/SmartReferenceSelectField";
import {UiComponent} from "../model/UiComponent";
import {
    UiComponentDataContext,
    UiComponentRendererPlugin,
    UiComponentRenderingContext,
    UiComponentRenderingProps,
    UiComponentStateContext,
    UiComponentValidationContext
} from "../UiComponentRendererPlugin";
import ErrorFieldPlaceholder from "./ErrorFieldPlaceholder";


interface SmartReferenceSelectFieldState {
    originalRelatedElement?: ElementDto;
    originalRelationshipId?: string;
    relatedElement?: ElementDto;
    elements?: ElementDto[];
}

export class SmartReferenceSelectFieldPlugin implements UiComponentRendererPlugin {

    private readonly graphApiClient = new GraphApiClient();
    private readonly elementApiClient = new ElementApiClient();
    private readonly relationshipApiClient = new RelationshipApiClient();

    getSupportedComponentTypes(): string[] {
        return [SMART_REFERENCE_SELECT_FIELD_COMPONENT_TYPE];
    }

    getInitialValues(component: UiComponent, dataContext: UiComponentDataContext, renderingContext: UiComponentRenderingContext): Promise<Record<string, any>> {
        const smartReferenceField = component as SmartReferenceSelectField;

        const referenceDefinition: ReferenceDefinitionDto = dataContext.referenceDefinitions[smartReferenceField.referenceDefinitionId];
        if (!referenceDefinition) {
            return Promise.resolve({});
        }

        const relationshipDetailsPromise = this.graphApiClient.findNeighbours([dataContext.element.identifier])
                .then(graph => {
                    return ReferenceDefinitionGraphSearcher.findRelatedElementsByReferenceDefinition(dataContext.element, referenceDefinition, graph);
                });

        const elementFilter: ElementFilterDto = {
            types: [referenceDefinition.relatedElementFilter.type],
            stereotypeId: referenceDefinition.relatedElementFilter.stereotypeId ?? undefined
        };
        const filteredElementsPromise = this.elementApiClient.findByFilter(elementFilter)
                .then(resultList => resultList.items);

        return Promise.all([relationshipDetailsPromise, filteredElementsPromise])
                .then(([relationshipDetails, filteredElements]) => {
                    const relationshipDetail = relationshipDetails.length > 0 ? relationshipDetails[0] : undefined;
                    const state: SmartReferenceSelectFieldState = {
                        originalRelatedElement: relationshipDetail?.element,
                        originalRelationshipId: relationshipDetail?.relationship.id,
                        relatedElement: relationshipDetail?.element,
                        elements: filteredElements
                    };
                    return {
                        [smartReferenceField.referenceDefinitionId]: state
                    }
                });
    }

    validate(component: UiComponent, dataContext: UiComponentDataContext, stateContext: UiComponentStateContext, validationContext: UiComponentValidationContext): Record<string, string> {
        const smartReferenceField = component as SmartReferenceSelectField;

        const referenceDefinition: ReferenceDefinitionDto = dataContext.referenceDefinitions[smartReferenceField.referenceDefinitionId];
        if (!referenceDefinition) {
            return {};
        }

        return {};
    }

    renderComponent({component, dataContext, stateContext, renderingContext}: UiComponentRenderingProps): ReactElement {
        const smartReferenceField = component as SmartReferenceSelectField;

        const referenceDefinition: ReferenceDefinitionDto = dataContext.referenceDefinitions[smartReferenceField.referenceDefinitionId];
        if (!referenceDefinition) {
            return (
                    <ErrorFieldPlaceholder key={smartReferenceField.id} error={t(StereotypesTranslationKey.UNKNOWN_REFERENCE)} />
            );
        }

        const state: SmartReferenceSelectFieldState = stateContext.values[smartReferenceField.referenceDefinitionId];
        if (!state) {
            return <Typography variant={"h5"}><Skeleton variant={"text"}/></Typography>;
        }

        const value = state.relatedElement;
        const elementOptions = state.elements ?? [];
        const error = stateContext.errors[smartReferenceField.referenceDefinitionId];
        const readOnly = smartReferenceField.readOnly || !stateContext.editMode;

        function onChange(newValue: ElementDto | null) {
            stateContext.onValuesChange({
                [smartReferenceField.referenceDefinitionId]: {
                    ...state,
                    relatedElement: newValue
                }
            });
        }

        return (
                <SingleSelectField
                        key={smartReferenceField.id}
                        label={referenceDefinition.name}
                        value={value}
                        onChange={onChange}
                        options={elementOptions}
                        getOptionLabel={option => option?.name ?? ""}
                        isOptionEqualToValue={(option, value) => option.identifier === value?.identifier}
                        errorMessage={error ? t(error) : undefined}
                        required={referenceDefinition.required}
                        readOnly={readOnly} />
        );
    }

    save(component: UiComponent, dataContext: UiComponentDataContext, stateContext: UiComponentStateContext): Promise<void>[] {
        const smartReferenceField = component as SmartReferenceSelectField;

        const referenceDefinition: ReferenceDefinitionDto = dataContext.referenceDefinitions[smartReferenceField.referenceDefinitionId];
        if (!referenceDefinition) {
            return [];
        }

        const state: SmartReferenceSelectFieldState | undefined = stateContext.values[smartReferenceField.referenceDefinitionId];
        if (!state) {
            return [];
        }
        const originalElement = state.originalRelatedElement;
        const originalRelationshipId = state.originalRelationshipId;
        const currentElement = state.relatedElement;

        const promises: Promise<void>[] = [];

        if (originalElement) {
            if ((!currentElement || currentElement.identifier !== originalElement.identifier) && originalRelationshipId) {
                promises.push(this.relationshipApiClient.deleteRelationship(originalRelationshipId));
                state.originalRelatedElement = undefined;
                state.originalRelationshipId = undefined;
            }
        }

        if (currentElement) {
            if (!originalElement || currentElement.identifier !== originalElement.identifier) {
                const relationship = SmartReferenceSelectFieldPlugin.buildRelationship(dataContext.element, currentElement, referenceDefinition);
                promises.push(this.relationshipApiClient.createRelationship(relationship));
                state.originalRelatedElement = currentElement;
                state.originalRelationshipId = relationship.identifier ?? undefined;
            }
        }

        return promises;
    }

    public static buildRelationship(baseElement: ElementDto, relatedElement: ElementDto, referenceDefinition: ReferenceDefinitionDto): RelationshipCreateDto {
        let sourceElement: ElementDto;
        let targetElement: ElementDto;
        if (referenceDefinition.relationshipDirection === RelationshipDirection.OUTGOING) {
            sourceElement = baseElement;
            targetElement = relatedElement;
        } else {
            sourceElement = relatedElement;
            targetElement = baseElement;
        }

        return {
            identifier: "id-" + UUIDUtils.randomUUID(),
            type: referenceDefinition.relationshipType,
            source: SmartReferenceSelectFieldPlugin.convertToConnectableConcept(sourceElement),
            target: SmartReferenceSelectFieldPlugin.convertToConnectableConcept(targetElement),
            acl: {
                canUpdate: true,
                canDelete: true
            }
        };
    }

    public static convertToConnectableConcept(element: ElementDto): ConnectableConceptInfoDto {
        return {
            conceptType: ConnectableConceptType.ELEMENT,
            identifier: element.identifier,
            name: element.name
        };
    }

}
